import {
  Button,
  Input,
  MinthrSelect,
  Sidebar,
  TextArea,
} from 'components/common';
import { Form, Formik, FormikHelpers, useFormikContext } from 'formik';
import useFetchData from 'hooks/useFetch';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchUsers } from './LeaveRequestComponents';
import {
  CreateCongeService,
  EditCongeService,
  GetConditionCongeService,
  GetHolidaySoldeService,
  GetNoticePeriodService,
  GetNumberofDaysService,
  GettypesService,
  // UserDailyWorkAverageService,
} from '../services/leaveService';
import { GetShiftUsersByFunction } from 'features/shift/services/shiftServices';
import {
  CreateCongeType,
  KeySettings,
  ReportRequest,
} from '../types/leaveRequestType';
import moment from 'moment';
import FileUpload from 'components/common/FileUpload';
import {
  appendToFormData,
  checkFileType,
  getIconByExtension,
  onUploadProgress,
} from 'utils/globalMethodes';
import { useCancelableRequest } from 'hooks/useCancelableRequest';
import { toast } from 'react-toastify';
import { useSidebarManager } from 'contexts/SidebarManagerContext';
import MinthrDateRangePicker from 'components/common/MinthrDateRangePicker';
import * as Yup from 'yup';
import { EnumProjectModule } from 'features/project/enums/enums';
import { getProjectsUser } from 'features/project/services/services';
import MintErrorMessage from 'components/common/MintErrorMessage';
import TruncateText from 'components/common/TruncateText';

// The backend puts an error text either in `message` or, for some leave
// errors (e.g. edit: not found / access denied), in `validationErrors[0]`.
const responseMessage = <T,>(response: ApiResponse<T>): string | undefined =>
  response.message || response.validationErrors?.[0]?.message || undefined;

interface DayRules {
  periods?: string[];
  daysOff?: { day: number; value: string }[];
}

// A day the employee can't take off: blocked period, or weekly / dated day
// off. Past dates and the notice period are not checked here: HR can force
// them and the backend validates the request.
const isBlockedDay = (date: moment.Moment, rules: DayRules): boolean => {
  const dateString = date.format('DD/MM/YYYY');
  if (rules.periods?.includes(dateString)) return true;
  return !!rules.daysOff?.some((item) =>
    item.value === 'All' ? item.day === date.day() : item.value === dateString,
  );
};

// Everything the day count depends on: a count is only valid for this key.
const daysCountKey = (values: CreateCongeType): string =>
  [
    values.fk_User,
    values.fk_RegimeType,
    moment(values.startDate).format('YYYY-MM-DD'),
    moment(values.endDate).format('YYYY-MM-DD'),
    values.morning,
    values.afternoon,
    values.startTime,
    values.endTime,
  ].join('|');

// Recounts the days whenever the period (or what the count depends on)
// changes, whatever changed it — so no selection is ever left uncounted.
const DaysCountSync: FC<{ onChange: (values: CreateCongeType) => void }> = ({
  onChange,
}) => {
  const { values } = useFormikContext<CreateCongeType>();
  const key = daysCountKey(values);
  useEffect(() => {
    onChange(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recount on key change only
  }, [key]);
  return null;
};

interface CreateCongeParamsProps {
  refetch?: () => void;
  initValues?: CreateCongeType;
  userId?: string;
  fromShift?: boolean;
  shiftStartDate?: string;
  shiftEndDate?: string;
  fonctionId?: string;
}
const CreateConge: FC<SideBarProps<CreateCongeParamsProps>> = ({
  sidebar,
  setSidebarWidth,
  sidebarWidth,
  params,
}) => {
  const { t } = useTranslation();
  const isFonctionsRow = !!params.fonctionId;
  const fonctionRowId = isFonctionsRow ? Number(params.fonctionId) : 0;
  const { data: usersAll } = useFetchData(fetchUsers, [], {
    enabled: !isFonctionsRow,
  });
  const { data: usersByFonction } = useFetchData(
    GetShiftUsersByFunction,
    [fonctionRowId],
    { enabled: isFonctionsRow && fonctionRowId > 0 },
  );
  const users = isFonctionsRow
    ? usersByFonction?.map((item) => ({
        label: item.text,
        value: item.value,
      }))
    : usersAll;
  const [typeConge, setTypeConge] = useState<SelectItem[]>();
  const [projectsList, setProjectData] = useState<SelectItem[]>([]);
  const [condition, setCondition] = useState<KeySettings | null>(null);
  const [solde, setSolde] = useState<string>('0');
  const [countDays, setCountDays] = useState<string>('');
  const [noticePeriod, setNoticePeriod] = useState<number>();
  const [daysOff, setdaysOff] = useState<
    {
      day: number;
      value: string;
    }[]
  >();
  const [filesNames, setFilesNames] = useState<FileType[]>(
    params.initValues?.filesNames ?? [],
  );
  const [deletedFileIds, setDeletedFileIds] = useState<number[]>([]);
  const [periods, setPeriods] = useState<string[]>();
  const [errorMsg, setErrMsg] = useState<string>();
  const [totalDays, setTotalDays] = useState<number | null>(null);
  // Backend reason when it refused the selected period's day count.
  const [daysCountMessage, setDaysCountMessage] = useState<string>();
  // daysCountKey of the period the current total belongs to; while it differs
  // from the form's period, the count is pending and submit stays blocked.
  const [countedKey, setCountedKey] = useState<string | null>(null);
  const lastCountRequestKey = useRef<string | null>(null);
  // Ids of the latest requests: older responses are dropped.
  const countRequestId = useRef(0);
  const soldeRequestId = useRef(0);
  const [, setProgress] = useState(0);
  const { closeSidebar } = useSidebarManager();
  const validationSchema = Yup.object().shape({
    fk_RegimeType: Yup.string().required(t('RequiredAttribute')),
    fk_User: Yup.string().required(t('RequiredAttribute')),
    description: condition?.DescriptionRequired
      ? Yup.string().required(t('RequiredAttribute'))
      : Yup.string().notRequired(),
    fk_Substitute: condition?.SubstituteRequired
      ? Yup.string().required(t('RequiredAttribute'))
      : Yup.string().notRequired(),
    files: condition?.JustificatifFilesRequired
      ? Yup.array().test(
          'files-required',
          t('RequiredAttribute'),
          (value) => (value?.length ?? 0) + filesNames.length > 0,
        )
      : Yup.array().notRequired(),
  });
  const { createRequest } = useCancelableRequest();

  useEffect(() => {
    if (params.initValues) {
      handelFecthSolde(params.initValues);
      handelFetchTypeConge(params.initValues.fk_User);
      fetchProjectData(params.initValues.fk_User);
      getNumberofDays(params.initValues);
    }
    if (params.userId) {
      handelFetchTypeConge(params.userId);
      fetchProjectData(params.userId);
    }
  }, []);
  const fetchProjectData = async (userId: string): Promise<void> => {
    const response = await getProjectsUser({
      userId: userId,
      module: EnumProjectModule.Expense,
    });
    if (response.success) {
      setProjectData(response.data);
    }
  };
  const handelFetchTypeConge = async (userId: string): Promise<void> => {
    const response = await GettypesService(userId);
    setTypeConge(response.types);
  };

  // Drops everything derived from the previous employee / leave type, so
  // nothing (balance, total, half-day, notice, blocked days…) leaks into the
  // new choice. In-flight responses are discarded through the request ids.
  const resetLeaveType = (
    setFieldValue: FormikHelpers<CreateCongeType>['setFieldValue'],
  ): void => {
    soldeRequestId.current++;
    countRequestId.current++;
    lastCountRequestKey.current = null;
    setErrMsg('');
    setSolde('0');
    setCountDays('');
    setTotalDays(null);
    setDaysCountMessage(undefined);
    setCountedKey(null);
    setCondition(null);
    setNoticePeriod(undefined);
    setdaysOff(undefined);
    setPeriods(undefined);
    setFieldValue('morning', false);
    setFieldValue('afternoon', false);
    setFieldValue('startTime', null);
    setFieldValue('endTime', null);
    setFieldValue('fk_Substitute', '');
    setFieldValue('projectId', '');
    setProjectData([]);
  };

  // Outdated responses (another employee / type picked meanwhile) are dropped.
  const handelFecthSolde = async (values: CreateCongeType): Promise<void> => {
    const requestId = ++soldeRequestId.current;
    const payload: ReportRequest = {
      typeId: values.fk_RegimeType,
      userId: values.fk_User,
      year: moment(values.startDate).year(),
      month: Number(moment(values.startDate).format('M')),
      fullYear: !(values.startDate && values.endDate),
      report: false,
    };
    const soldeRes = await GetHolidaySoldeService(payload);
    const noticePeriodRes = await GetNoticePeriodService(
      Number(values.fk_RegimeType),
    );
    const conditioReponse = await GetConditionCongeService({
      id: values.fk_User,
      fk_RegimeType: Number(values.fk_RegimeType),
    });
    if (requestId !== soldeRequestId.current) return;

    // Reset on failure too: a stale notice from the previous type would
    // stay on screen.
    setNoticePeriod(
      noticePeriodRes.success ? Number(noticePeriodRes.value) : undefined,
    );

    if (conditioReponse) {
      setdaysOff(conditioReponse.value);
      setPeriods(Object.keys(conditioReponse.periode));
      setCondition(conditioReponse.key);
      if (conditioReponse.key.IsProjectLinked) {
        fetchProjectData(values.fk_User as string);
      }
    }

    if (soldeRes.success) {
      // The unit comes from this type, not from the previous one's state.
      const calcByHours = conditioReponse?.key.CalcByHours ?? false;
      setSolde(
        calcByHours || Number(values.fk_RegimeType) === 5
          ? soldeRes.value !== 'illimité'
            ? `${soldeRes.value} h`
            : `${soldeRes.value}`
          : String(soldeRes.value),
      );
    } else {
      setErrMsg(soldeRes.message);
    }
  };

  const getNumberofDays = async (values: CreateCongeType): Promise<void> => {
    if (!values.fk_RegimeType) {
      return;
    }
    const key = daysCountKey(values);
    if (key === lastCountRequestKey.current) return;
    lastCountRequestKey.current = key;
    const requestId = ++countRequestId.current;
    const response = await GetNumberofDaysService({
      startDate: values.startDate,
      endDate: values.endDate,
      morning: values.morning,
      afternoon: values.afternoon,
      user_Id: values.fk_User,
      fk_RegimeType: Number(values.fk_RegimeType),
      startTime: values.startTime ? `${values.startTime}:00` : null,
      endTime: values.endTime ? `${values.endTime}:00` : null,
    }).catch((error) => {
      // Let the same period be counted again on the next change.
      if (requestId === countRequestId.current) {
        lastCountRequestKey.current = null;
      }
      throw error;
    });
    if (requestId !== countRequestId.current) return;
    setCountedKey(key);
    setTotalDays(response.success ? Number(response.value) : null);
    setDaysCountMessage(
      response.success
        ? undefined
        : (responseMessage(response) ?? t('TheSelectedDaysIsNotValid')),
    );
    // A refused count must not leave the previous total on screen.
    if (!response.success) setCountDays('');
    if (response.success) {
      if (response.action === 'CalcByHours') {
        // const res = await UserDailyWorkAverageService(
        //   values.fk_User,
        //   Number(values.fk_RegimeType),
        // );
        const formatHours = (hours: number): string => {
          if (hours === 0) return '0h';

          const totalMinutes = Math.round(hours * 60);
          const hoursPart = Math.floor(totalMinutes / 60);
          const minutesPart = totalMinutes % 60;

          // Only show minutes if they exist
          if (minutesPart > 0) {
            return hoursPart + 'h ' + minutesPart + 'm';
          }
          return hoursPart + 'h';
        };
        setCountDays(
          `${Number(response.value)}  ( ${formatHours(Number(response.data))} )`,
        );
        // if (res) {
        //   if (res.success) {
        //     setCountDays(
        //       `${Number(response.value) / Number(res.value)}  ( ${response.value}h )`,
        //     );
        //   }
        // }
      } else {
        setCountDays(response.value as string);
      }
    }
  };

  // Memoized: the picker redraws its open calendar when these change.
  const isInvalidDate = useCallback(
    (date: moment.Moment): boolean => isBlockedDay(date, { periods, daysOff }),
    [periods, daysOff],
  );

  // The selection can't be requested: no worked day in it, or the backend
  // refused the count. Exact-time types only get a count once both times are
  // entered.
  const hasNoWorkedDay = (values: CreateCongeType): boolean =>
    (totalDays === 0 || !!daysCountMessage) &&
    !!values.fk_RegimeType &&
    (!condition?.IsExcatTimeRequired ||
      (!!values.startTime && !!values.endTime));

  // Submit needs a type and a day count that belongs to the selected period
  // and allows it.
  const isSubmitBlocked = (values: CreateCongeType): boolean =>
    !values.fk_RegimeType ||
    countedKey !== daysCountKey(values) ||
    hasNoWorkedDay(values);

  const handelSubmit = async (values: CreateCongeType): Promise<void> => {
    // Enter in a field submits the form even when the button is disabled.
    if (isSubmitBlocked(values)) return;
    const response =
      values.id != 0
        ? await EditCongeService(
            appendToFormData({ ...values, filesToDelete: deletedFileIds }),
            onUploadProgress(setProgress),
            createRequest(),
          )
        : await CreateCongeService(
            appendToFormData(values),
            onUploadProgress(setProgress),
            createRequest(),
          );
    if (response.success) {
      // Saved, but with a warning (e.g. approvers' email not sent).
      if (response.message) {
        toast.warn(response.message);
      } else {
        toast.success(t('Default.succesMessage'));
      }
      closeSidebar(CreateConge);
      if (params.refetch) {
        params.refetch();
      }
    } else {
      setErrMsg(responseMessage(response));
    }
  };

  const handelDeleteFile = async (id: number): Promise<void> => {
    setDeletedFileIds((prev) => [...prev, id]);
    setFilesNames((prev) => prev.filter((item) => id != item.id));
  };
  const handleDownloadFile = (path: string, name: string): void => {
    const link = document.createElement('a');
    link.href = `/upload/VueFileByName/${name}`;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  return (
    <Sidebar
      size="m"
      type="primary"
      sidebar={sidebar}
      setSidebarWidth={setSidebarWidth}
      sidebarWidth={sidebarWidth}
    >
      <div className="minthr-flex minthr-flex-items-center minthr-pb-2 minthr-mb-6 minthr-border-b minthr-border-b-3">
        <div className="minthr-icon-bg minthr-icon-bg-l minthr-icon-bg-secondary minthr-icon-circle minthr-mr-3">
          <img className="minthr-icon-m" src="/icons/calendar.svg" alt="" />
        </div>
        <h3>
          {!params.userId
            ? !params.initValues
              ? t('Conge.Create.Title')
              : t('Conge.Profil.Update.Title')
            : t('Conge.Profil.Create.Title')}
        </h3>

        <button
          onClick={() => {
            closeSidebar(CreateConge);
          }}
          className="minthr-sidebar-close"
          type="button"
        >
          <img src="/icons/close.svg" alt="" />
        </button>
      </div>
      <Formik
        initialValues={
          params.initValues
            ? { ...params.initValues, filesNames: null }
            : {
                id: 0,
                fk_User: params.userId ?? '',
                fk_RegimeType: '',
                startDate: params.shiftStartDate
                  ? params.shiftStartDate
                  : moment().toISOString(),
                endDate: params.shiftEndDate
                  ? params.shiftEndDate
                  : moment().toISOString(),
                afternoon: false,
                morning: false,
                files: [],
                projectId: '',
                filesNames: null,
                description: '',
                startTime: null,
                endTime: null,
                fk_Substitute: '',
              }
        }
        validationSchema={validationSchema}
        onSubmit={handelSubmit}
      >
        {({ values, setFieldValue, isSubmitting }) => (
          <Form>
            {errorMsg && <MintErrorMessage message={errorMsg} />}

            <>
              {values.id == 0 && (
                <>
                  {(!params.userId || params.fromShift) && (
                    <div className="row g-0 minthr-mb-5 minthr-mobile-mb-0">
                      <div className="col-sm-12 minthr-mobile-mb-5">
                        <MinthrSelect
                          value={values.fk_User}
                          name="fk_User"
                          label={t('Conge.Create.f.users')}
                          options={
                            users?.map((item) => ({
                              label: item.label,
                              value: item.value,
                            })) ?? []
                          }
                          onchange={(e: SelectOptionResponse) => {
                            // The type is cleared: nothing is fetched until a
                            // new one is picked.
                            resetLeaveType(setFieldValue);
                            setFieldValue('fk_User', e.value);
                            setFieldValue('fk_RegimeType', '');
                            handelFetchTypeConge(e.value as string);
                          }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="row g-0 minthr-mb-5">
                    <div className="col-sm-12 minthr-mobile-mb-5 conge-sidebar-step1">
                      <MinthrSelect
                        name="fk_RegimeType"
                        value={values.fk_RegimeType}
                        label={t('Conge.Create.f.typeConge')}
                        options={
                          typeConge?.map((item) => ({
                            label: item.text,
                            value: item.value,
                          })) ?? []
                        }
                        onchange={async (e: SelectItem) => {
                          // Same type again: nothing to reset (and the
                          // unchanged period would never be recounted).
                          if (String(e.value) === String(values.fk_RegimeType))
                            return;
                          // The day count follows through DaysCountSync.
                          resetLeaveType(setFieldValue);
                          setFieldValue('fk_RegimeType', e.value);
                          await handelFecthSolde({
                            ...values,
                            fk_RegimeType: e.value as string,
                            morning: false,
                            afternoon: false,
                            startTime: null,
                            endTime: null,
                          });
                        }}
                      />
                    </div>
                  </div>
                </>
              )}

              {users &&
                users.length > 0 &&
                values.fk_RegimeType &&
                condition?.SubstituteVisible && (
                  <div className="row g-0 minthr-mb-5">
                    <div className="col-sm-12 minthr-mobile-mb-5 ">
                      <MinthrSelect
                        name="fk_Substitute"
                        value={values.fk_Substitute ?? ''}
                        label={t('Conge.Create.f.substitute')}
                        options={
                          users?.map((item) => ({
                            label: item.label,
                            value: item.value,
                          })) ?? []
                        }
                        onchange={(e: SelectItem) => {
                          setFieldValue('fk_Substitute', e.value);
                        }}
                      />
                    </div>
                  </div>
                )}
              {projectsList &&
                projectsList.length > 0 &&
                values.fk_RegimeType &&
                condition?.IsProjectLinked && (
                  <div className="row g-0 minthr-mb-5 minthr-mobile-mb-0">
                    <div className="col-sm-12 minthr-mobile-mb-5">
                      <MinthrSelect
                        key={`project-${values.fk_User}-${projectsList.length}`}
                        options={projectsList.map((item) => ({
                          label: item.text,
                          value: item.value,
                        }))}
                        name="projectId"
                        value={values.projectId ?? ''}
                        label={t('ProjectName')}
                        onchange={(e: SelectOptionResponse) => {
                          setFieldValue('projectId', e.value);
                        }}
                      />
                    </div>
                  </div>
                )}

              <DaysCountSync onChange={getNumberofDays} />
              {/* Balance and total only mean something for a chosen type. */}
              {values.fk_RegimeType && (
                <div className="row g-0 minthr-mb-2 conge-sidebar-step2">
                  <div className="col-sm-5 ">
                    {t('Holiday.YourSoldIs')} :{' '}
                    <span className="solde"> {solde}</span>
                  </div>
                  <div className="col-sm-7">
                    {t('Holiday.TotalDays')} :{' '}
                    <span className="totalday">{countDays}</span>
                  </div>
                </div>
              )}

              <>
                <div className="row g-0  minthr-mb-3  ">
                  <div className="col-sm-12">
                    <MinthrDateRangePicker
                      startDateName="startDate"
                      endDateName="endDate"
                      saveFormat="YYYY-MM-DD"
                      onChange={(startDate, endDate) => {
                        // The picker also fires when it closes on the same
                        // days: keep the server error until the period
                        // really changes.
                        const periodChanged =
                          !startDate?.isSame(values.startDate, 'day') ||
                          !endDate?.isSame(values.endDate, 'day');
                        if (!periodChanged) return;
                        setErrMsg('');
                        if (startDate && endDate) {
                          getNumberofDays({
                            ...values,
                            startDate: startDate?.format('YYYY-MM-DD'),
                            endDate: endDate?.format('YYYY-MM-DD'),
                          });
                          handelFecthSolde({
                            ...values,
                            startDate: startDate?.format('YYYY-MM-DD'),
                            endDate: endDate?.format('YYYY-MM-DD'),
                          });
                        }
                      }}
                      customView={(startDate, endDate) => (
                        <div className="custom-view">
                          <input
                            disabled={!values.fk_RegimeType}
                            // Display only: the period is picked in the
                            // calendar, and Enter here must not submit.
                            readOnly
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') e.preventDefault();
                            }}
                            className={`w-100 minthr-border-rounded minthr-border minthr-text-align-center minthr-p-3 demandePeriod ${!values.fk_RegimeType ? 'minthr-color-gray ' : 'minthr-color-primary'}  `}
                            value={
                              startDate?.format('L') +
                              ' - ' +
                              endDate?.format('L')
                            }
                          />
                        </div>
                      )}
                      isInvalidDate={isInvalidDate}
                      disabled={!values.fk_RegimeType}
                      customViewClassName={
                        !values.fk_RegimeType ? 'minthr-cursor-not-allowed' : ''
                      }
                    />
                    {hasNoWorkedDay(values) &&
                      countedKey === daysCountKey(values) && (
                        <MintErrorMessage
                          className="minthr-mt-3"
                          message={
                            daysCountMessage ?? t('TheSelectedDaysIsNotValid')
                          }
                        />
                      )}
                    {condition?.IsExcatTimeRequired && (
                      <div
                        className="minthr-flex minthr-flex-items-center minthr-flex-space-between minthr-p-y3 minthr-bg-light-gray minthr-border-rounded minthr-mt-3"
                        id="SpecificTimeSelector"
                      >
                        <div className="col-sm-6 minthr-p-2">
                          <Input
                            type="time"
                            label={t('F.StartTime')}
                            name="startTime"
                            active
                            onChange={(e) => {
                              setFieldValue('startTime', e.target.value);
                              getNumberofDays({
                                ...values,
                                startTime: e.target.value,
                              });
                            }}
                          />
                        </div>

                        <div className="col-sm-6 minthr-p-2">
                          <Input
                            type="time"
                            label={t('F.EndTime')}
                            name="endTime"
                            active
                            onChange={(e) => {
                              setFieldValue('endTime', e.target.value);
                              getNumberofDays({
                                ...values,
                                endTime: e.target.value,
                              });
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {condition?.IsHalfDay && (
                      <div
                        className="minthr-flex minthr-flex-items-center minthr-flex-space-between minthr-p-3 minthr-bg-light-gray minthr-border-rounded "
                        id="halfDaySelector"
                      >
                        <div className="minthr-chackbox">
                          <input
                            type="checkbox"
                            className="minthr-form-control"
                            name="morning"
                            checked={values.morning}
                          />
                          <label
                            className="minthr-form-label text-s minthr-py-1"
                            htmlFor="morning"
                            onClick={() => {
                              setFieldValue('morning', !values.morning);
                              getNumberofDays({
                                ...values,
                                morning: !values.morning,
                              });
                            }}
                          >
                            {t('Conge.Create.f.Matine')}
                          </label>
                        </div>

                        <div className="minthr-chackbox">
                          <input
                            type="checkbox"
                            className="minthr-form-control"
                            name="afternoon"
                            checked={values.afternoon}
                          />
                          <label
                            className="minthr-form-label text-s minthr-py-1"
                            htmlFor="afternoon"
                            onClick={() => {
                              const checked = !values.afternoon;
                              setFieldValue('afternoon', checked);
                              getNumberofDays({
                                ...values,
                                afternoon: checked,
                              });
                            }}
                          >
                            {t('Conge.Create.f.AfterNoon')}
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                  {noticePeriod && noticePeriod > 0 ? (
                    <label className="minthr-form-label text-s minthr-py-1 ">
                      {t('notice.period').replace('{0}', String(noticePeriod))}
                    </label>
                  ) : (
                    ''
                  )}
                </div>

                <div className="row g-0 minthr-mb-2">
                  <div className="col-sm-12">
                    <FileUpload
                      name="files"
                      allowedFileTypes={[
                        'image/*',
                        'pdf',
                        'jpeg',
                        'png',
                        'jpg',
                        'doc',
                        'docx',
                        'xls',
                        'xlsx',
                        'ppt',
                        'pptx',
                        'txt',
                      ]}
                      initialFiles={values.files}
                      existingFiles={filesNames}
                      onCustomDelete={handelDeleteFile}
                      labelDesc={t('Uplaod.file')}
                      existingFilesContainerClassName="minthr-hide"
                    />
                    {filesNames &&
                      filesNames.length > 0 &&
                      filesNames.map((file) => (
                        <a
                          className="minthr-flex minthr-flex-items-center w-100 minthr-bg-light-gray minthr-text-decoration-none minthr-p-2 minthr-border-rounded minthr-mb-3"
                          key={file.id}
                        >
                          <img
                            className="minthr-icon-xl minthr-mr-2"
                            src={`/icons/${getIconByExtension(file.name)}`}
                            alt=""
                          />
                          <div className="minthr-mr-6 minthr-pr-3 minthr-overflow-hidden">
                            <TruncateText
                              tag="h6"
                              className="minthr-text-truncate"
                              maxCharacters={33}
                              showTooltip
                            >
                              {file.name}
                            </TruncateText>
                          </div>
                          <button
                            type="button"
                            className="minthr-p-0 minthr-border-none minthr-bg-transparent minthr-ml-auto minthr-mr-2 file-delete"
                            onClick={() => {
                              handelDeleteFile(file.id);
                            }}
                          >
                            <img
                              className="minthr-icon-s minthr-icon-gray"
                              src="/icons/delete.svg"
                              alt=""
                            />
                          </button>
                          <button
                            type="button"
                            className={`minthr-p-0 minthr-border-none minthr-bg-transparent minthr-ml-1 minthr-mr-2 ${checkFileType(file.name) == 'pdf' ? 'pdf-vue' : checkFileType(file.name) == 'image' ? 'img-vue' : 'DownLoadFile'}`}
                            onClick={() => {
                              handleDownloadFile(file.path, file.name);
                            }}
                          >
                            <img
                              className="minthr-icon-s minthr-icon-gray"
                              src="/icons/download.svg"
                              alt=""
                            />
                          </button>
                        </a>
                      ))}
                  </div>
                </div>

                <div className="row g-0 minthr-mb-5">
                  <div className="col-sm-12 conge-sidebar-step5">
                    <TextArea
                      name="description"
                      label={t('Conge.Create.f.Remarque')}
                    />
                  </div>
                </div>
              </>
              <div className="minthr-sidebar-footer minthr-box-shadow minthr-flex minthr-flex-items-center minthr-flex-items-end minthr-py-4 minthr-mx-n5 minthr-px-6 minthr-mt-auto">
                <button
                  onClick={() => {
                    closeSidebar(CreateConge);
                  }}
                  type="button"
                  className="minthr-btn minthr-btn-outline minthr-btn-primary minthr-ml-auto minthr-sidebar-close"
                >
                  {t('cancel')}
                </button>
                <Button
                  loading={isSubmitting}
                  disabled={isSubmitBlocked(values)}
                  type="submit"
                  className={`minthr-btn ${isSubmitBlocked(values) ? 'minthr-btn-disabled' : 'minthr-btn-primary'} minthr-ml-6 conge-sidebar-step6`}
                >
                  {t('submit')}
                </Button>
              </div>
            </>
          </Form>
        )}
      </Formik>
    </Sidebar>
  );
};

export default CreateConge;
